import { Fragment, useRef, useState } from 'react';
import { Order, OrderInternalNote } from '../../../clients/quipli-service/order/order';
import { useAPI } from '../../../clients/quipli-service/quipli-service-client';
import { Collapsible, CollapsibleRef } from '../../../components/collapsible/collapsible';
import { toast } from '../../../util/toasts';
import { OrderSection } from '../order-section/order-section';
import { Typography } from '../../../components/typography/typography';
import Icon from '../../../components/icons/icon';
import { TextArea } from '../../../components/textarea/textarea';
import { Spinner } from '../../../components/spinner/spinner';
import { Button } from '../../../components/form/button';
import { useAdmin } from '../../../hooks/use-admin-context';

export const OrderNotes = ({ order, refresh }: { order: Order; refresh: () => Promise<Order> }) => {
  return (
    <>
      <NormalNotes orderId={order.id} content={order.notes} refresh={refresh} />
      <OrderInternalNotes orderId={order.id} notes={order.internalNotes} />
    </>
  );
};

const NormalNotes = ({
  orderId,
  content,
  refresh,
}: {
  orderId: number;
  content: string;
  refresh: () => Promise<Order>;
}) => {
  const { updateOrderNotes } = useAPI();

  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [note, setNote] = useState<string>(content);

  const submit = async () => {
    if (isLoading) return;

    setIsLoading(true);

    try {
      await updateOrderNotes(orderId, note);
      await refresh();
      toast('Order note updated sucessfully.');
      setIsEditing(false);
    } catch (ex: any) {
      toast(ex.message.msg || ex.message, 'error');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <OrderSection
      header={
        <div className="flex items-center justify-between">
          <Typography type="text-md" weight="medium" className="text-grey-800">
            Order Notes
          </Typography>

          {content.length > 0 && (
            <Button
              className="button-tertiary circle !p-0"
              onClick={() => {
                setIsEditing(isEditing ? false : true);
              }}
            >
              <Icon icon={isEditing ? 'Close' : 'Edit'} stroke="var(--grey-400)" />
            </Button>
          )}
        </div>
      }
    >
      <div className="flex flex-col gap-[8px] pt-[8px]">
        {content.length > 0 && !isEditing ? (
          <Typography type="text-sm" className="text-grey-800 whitespace-pre-line">
            {content}
          </Typography>
        ) : (
          <TextArea
            className="w-full h-full resize-none bg-grey-50 text-sm"
            placeholder="Add notes"
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
        )}

        {isLoading ? (
          <Spinner />
        ) : (
          note != content && (
            <div className="button-group" onClick={submit}>
              <Button className="button-secondary">Update</Button>
            </div>
          )
        )}
      </div>
    </OrderSection>
  );
};

const OrderInternalNotes = ({ orderId, notes }: { orderId: number; notes: OrderInternalNote[] }) => {
  const { addOrderInternalNote } = useAPI();

  const ref = useRef<CollapsibleRef>(null);

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [internalNotes, setInternalNotes] = useState<OrderInternalNote[]>(notes);
  const [note, setNote] = useState<string>('');

  const submit = async () => {
    if (note.length === 0) return;

    setIsLoading(true);

    try {
      const res = await addOrderInternalNote(orderId, note);
      setInternalNotes((prev) => [...prev, res]);

      toast('Internal note added sucessfully');
      setNote('');
      ref.current?.toggle(true);
    } catch (ex: any) {
      toast(ex.message.msg || ex.message, 'error');
    }

    setIsLoading(false);
  };

  const reload = (index: number, updatedNote: OrderInternalNote) => {
    const newNotes = [...internalNotes];
    newNotes[index] = updatedNote;
    setInternalNotes(newNotes);
  };

  return (
    <>
      <OrderSection>
        <Collapsible
          openAtStart
          ref={ref}
          trigger={(isOpen) => (
            <div className="flex justify-between items-center">
              <Typography type="text-md" weight="medium" className="text-grey-800">
                Internal Notes
              </Typography>

              <Icon icon={isOpen ? 'ChevronUp' : 'ChevronDown'} size="small" />
            </div>
          )}
          afterHiddenContent={
            <div className="flex flex-col gap-[8px] pt-[8px]">
              <TextArea
                className="w-full h-full resize-none bg-grey-50 text-sm"
                autoResize={false}
                placeholder="Add a note"
                value={note}
                onChange={(e) => setNote(e.target.value)}
              />

              {isLoading ? (
                <Spinner />
              ) : (
                note.length > 0 && (
                  <div className="button-group">
                    <Button className="button-secondary" onClick={submit}>
                      Add Note
                    </Button>
                  </div>
                )
              )}
            </div>
          }
        >
          <div className="flex flex-col gap-[8px]">
            {internalNotes.map((note, idx) => (
              <Fragment key={idx}>
                <InternalNote indexNote={idx} note={note} reload={reload} />
              </Fragment>
            ))}
          </div>
        </Collapsible>
      </OrderSection>
    </>
  );
};

const InternalNote = ({
  indexNote,
  note,
  reload,
}: {
  indexNote: number;
  note: OrderInternalNote;
  reload: (index: number, updatedNote: OrderInternalNote) => void;
}) => {
  const admin = useAdmin();
  const { updateOrderInternalNote } = useAPI();

  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [isLoading, setLoading] = useState<boolean>(false);
  const [value, setValue] = useState<string>(note.comment || '');

  const updateNote = async () => {
    if (value.length === 0 || isLoading) return;

    setLoading(true);

    try {
      const res = await updateOrderInternalNote(note.orderId, note.id, value);

      setIsEditing(false);

      reload(indexNote, res);
      toast('Internal note updated sucessfully.');
    } catch (error: any) {
      toast(error.message.msg || error.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="rounded-[8px] border border-solid border-grey-200 p-[14px] first:mt-[8px]">
      <div className="flex items-start justify-between">
        <div className="relative [&>*]:inline">
          <Typography type="text-xs" className="text-grey-800 capitalize">
            {note.author.adminFullName}.
          </Typography>
          <Typography type="text-xs" className="text-grey-500 !ml-[6px]">
            {note.createdAt.toFormat('ff')}
          </Typography>
        </div>

        {(admin.id === note.author.adminId || admin.isSuperAdmin) && (
          <Button
            className="button-tertiary circle"
            onClick={() => {
              setIsEditing(isEditing ? false : true);
            }}
          >
            <Icon icon={isEditing ? 'Close' : 'Edit'} stroke="var(--grey-400)" />
          </Button>
        )}
      </div>

      <div>
        {isEditing ? (
          <div className="flex flex-col gap-[8px]">
            <TextArea
              value={value}
              onChange={({ target }) => setValue(target.value)}
              className="border-none w-full font-sans text-sm"
              autoFocus={true}
            />

            <div className="button-group">
              {isLoading ? (
                <Spinner />
              ) : (
                <>
                  <Button className="button-secondary" onClick={updateNote}>
                    Update
                  </Button>
                </>
              )}
            </div>
          </div>
        ) : (
          <Typography type="text-sm" className="text-grey-900 whitespace-pre-line">
            {note.comment}
          </Typography>
        )}
      </div>
    </div>
  );
};
